import { useCallback, useEffect, useState } from 'react';
import {
  MAX_ANSWER_LENGTH,
  SUGGESTED_TOPIC_GROUPS,
  mergeTopics,
  type AdminQuestionView,
  type Difficulty,
  type QuestionFormat,
} from '@quiz/shared';
import { api, ApiError } from '../lib/api.js';
import { MediaField } from './MediaField.js';

export function QuestionBank({
  token,
  rounds,
  onAdd,
}: {
  token: string;
  rounds: { id: string; title: string }[];
  onAdd?: (questionId: string, roundId: string) => Promise<void>;
}) {
  const [questions, setQuestions] = useState<AdminQuestionView[]>([]);
  const [topics, setTopics] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [topic, setTopic] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [format, setFormat] = useState('');
  const [review, setReview] = useState('');
  const [writing, setWriting] = useState(false);
  const [editing, setEditing] = useState<AdminQuestionView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.set('q', search);
    if (topic) params.set('topic', topic);
    if (difficulty) params.set('difficulty', difficulty);
    if (format) params.set('format', format);
    if (review) params.set('needsReview', review);
    try {
      const res = await api<{ questions: AdminQuestionView[]; topics: string[] }>(
        `/api/admin/questions?${params}`,
        { token },
      );
      setQuestions(res.questions);
      setTopics(res.topics);
      setError(null);
    } catch (err) {
      // Otherwise a failed load is indistinguishable from "no questions match",
      // which sends somebody hunting for a filter they never set.
      setError(err instanceof ApiError ? err.message : 'Could not load the question bank.');
    }
  }, [token, search, topic, difficulty, format, review]);

  useEffect(() => {
    const id = setTimeout(() => void load(), 250);
    return () => clearTimeout(id);
  }, [load]);

  return (
    <aside className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-bold">Question bank</h2>
        <button
          onClick={() => setWriting(true)}
          className="rounded-lg bg-ink-2 px-3 py-1.5 text-sm hover:bg-ink-3"
        >
          + Write new
        </button>
      </div>

      {(writing || editing) && (
        <QuestionForm
          token={token}
          // Editing loads the existing values; writing starts blank.
          existing={editing}
          onDone={async () => {
            setWriting(false);
            setEditing(null);
            await load();
          }}
          onCancel={() => {
            setWriting(false);
            setEditing(null);
          }}
        />
      )}

      {error && <p className="rounded-lg bg-stop/15 p-3 text-sm text-stop">{error}</p>}

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search prompts and answers"
        className="w-full rounded-lg bg-ink-2 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-sky"
      />
      <div className="flex gap-2">
        {/* Suggested topics plus whatever is actually in use, so a topic added
            last month is as easy to pick as a shipped one. */}
        <Select value={topic} onChange={setTopic} options={mergeTopics(topics)} placeholder="Any topic" />
        <Select
          value={difficulty}
          onChange={setDifficulty}
          options={['EASY', 'MEDIUM', 'HARD']}
          placeholder="Any level"
        />
        <Select
          value={format}
          onChange={setFormat}
          options={['MULTIPLE_CHOICE', 'FREE_TEXT']}
          placeholder="Any format"
        />
        {/* Bulk-imported questions arrive flagged, and a bank of 500 is only
            useful if you can find the unchecked ones. Written out rather than
            passed through Select, because "true" is not a label. */}
        <select
          value={review}
          onChange={(e) => setReview(e.target.value)}
          className="min-w-0 flex-1 rounded-lg bg-ink-2 px-2 py-2 text-xs outline-none"
        >
          <option value="">Checked or not</option>
          <option value="true">Needs checking</option>
          <option value="false">Checked</option>
        </select>
      </div>

      <ul className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
        {questions.map((q) => (
          <li key={q.id} className="rounded-lg bg-ink-2 p-3 text-sm">
            <p className="font-medium">{q.prompt}</p>
            <p className="mt-1 text-xs text-go">{q.correctAnswer}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted">
              {/* Loud on purpose: an unchecked question in a paid round is the
                  thing this flag exists to stop. */}
              {q.needsReview && (
                <span className="rounded bg-flag/20 px-1.5 py-0.5 font-semibold text-flag">
                  needs checking
                </span>
              )}
              {q.topic && <span className="rounded bg-ink px-1.5 py-0.5">{q.topic}</span>}
              <span>{q.difficulty.toLowerCase()}</span>
              <span>{q.format === 'FREE_TEXT' ? 'text' : 'choice'}</span>
              {/* So you do not ask the same question two months running. */}
              <span className={q.lastUsedAt ? 'text-flag' : ''}>
                {q.lastUsedAt
                  ? `last used ${new Date(q.lastUsedAt).toLocaleDateString('en-GB')}`
                  : 'never used'}
              </span>
            </div>
            {onAdd && rounds.length > 0 && (
              <select
                value=""
                onChange={(e) => e.target.value && void onAdd(q.id, e.target.value)}
                className="mt-2 w-full rounded bg-ink px-2 py-1.5 text-xs outline-none"
              >
                <option value="">Add to round…</option>
                {rounds.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.title}
                  </option>
                ))}
              </select>
            )}

            <div className="mt-2 flex items-center gap-3 text-xs">
              <button
                onClick={() => {
                  setError(null);
                  setWriting(false);
                  setEditing(q);
                }}
                className="text-sky underline"
              >
                Edit
              </button>
              <button
                onClick={async () => {
                  if (!confirm(`Delete "${clip(q.prompt)}"? This cannot be undone.`)) return;
                  setError(null);
                  try {
                    await api(`/api/admin/questions/${q.id}`, { method: 'DELETE', token });
                    await load();
                  } catch (err) {
                    // The server refuses to delete a question a past event graded
                    // against; surface that rather than a bare failure.
                    setError(err instanceof ApiError ? err.message : 'Could not delete that.');
                  }
                }}
                className="text-stop underline"
              >
                Delete
              </button>
              {/* Checking 500 imported questions one dialog at a time would be
                  its own reason not to do it, so clearing the flag is one tap
                  and does not open the editor. */}
              {q.needsReview && (
                <button
                  onClick={async () => {
                    setError(null);
                    try {
                      await api(`/api/admin/questions/${q.id}`, {
                        method: 'PATCH',
                        token,
                        body: { ...toQuestionBody(q), needsReview: false },
                      });
                      await load();
                    } catch (err) {
                      setError(err instanceof ApiError ? err.message : 'Could not mark that checked.');
                    }
                  }}
                  className="text-go underline"
                >
                  Mark checked
                </button>
              )}
              {q.usageCount > 0 && (
                <span className="text-muted">in {q.usageCount} round{q.usageCount === 1 ? '' : 's'}</span>
              )}
            </div>
          </li>
        ))}
        {questions.length === 0 && (
          <li className="py-6 text-center text-sm text-muted">Nothing matches.</li>
        )}
      </ul>
    </aside>
  );
}

function Select({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="min-w-0 flex-1 rounded-lg bg-ink-2 px-2 py-2 text-xs outline-none"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {/* Only the enum-shaped values need tidying; topic names are already
              written the way a person would read them. */}
          {/^[A-Z_]+$/.test(o) ? o.toLowerCase().replace(/_/g, ' ') : o}
        </option>
      ))}
    </select>
  );
}

function QuestionForm({
  token,
  existing,
  onDone,
  onCancel,
}: {
  token: string;
  existing?: AdminQuestionView | null;
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  const [format, setFormat] = useState<QuestionFormat>(existing?.format ?? 'MULTIPLE_CHOICE');
  const [prompt, setPrompt] = useState(existing?.prompt ?? '');
  const [options, setOptions] = useState(() => {
    const base = existing?.options ?? [];
    // Always leave a spare slot or two so options can be added while editing.
    return [...base, '', '', '', ''].slice(0, Math.max(4, base.length + 1));
  });
  const [correctAnswer, setCorrectAnswer] = useState(existing?.correctAnswer ?? '');
  const [accepted, setAccepted] = useState((existing?.acceptedAnswers ?? []).join(', '));
  const [topic, setTopic] = useState(existing?.topic ?? '');
  const [difficulty, setDifficulty] = useState<Difficulty>(existing?.difficulty ?? 'MEDIUM');
  /* Carried explicitly. The create schema defaults it to false, so a form that
     simply omitted it cleared the flag on every save — which quietly marked an
     imported question checked because somebody fixed its typo. */
  const [needsReview, setNeedsReview] = useState(existing?.needsReview ?? false);
  const [media, setMedia] = useState(existing?.media ?? null);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    try {
      await api(existing ? `/api/admin/questions/${existing.id}` : '/api/admin/questions', {
        method: existing ? 'PATCH' : 'POST',
        token,
        body: {
          format,
          prompt,
          options: format === 'MULTIPLE_CHOICE' ? options.filter(Boolean) : [],
          // Explicit either way. Omitting it leaves whatever is attached in
          // place, so removing a picture in the form would not remove it --
          // the same trap needsReview fell into.
          mediaId: media?.id ?? null,
          correctAnswer,
          acceptedAnswers:
            format === 'FREE_TEXT'
              ? accepted.split(',').map((s) => s.trim()).filter(Boolean)
              : [],
          topic: topic || null,
          difficulty,
          needsReview,
        },
      });
      await onDone();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? [err.message, ...(asDetails(err.details) ?? [])].join(' ')
          : 'Could not save.',
      );
    }
  }

  return (
    <div className="space-y-2 rounded-lg bg-ink-2 p-3 text-sm">
      {existing && (
        <p className="text-xs uppercase tracking-wide text-muted">
          Editing &mdash; changes apply to every quiz using this question
        </p>
      )}
      <div className="flex gap-2">
        {(['MULTIPLE_CHOICE', 'FREE_TEXT'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFormat(f)}
            className={`flex-1 rounded px-2 py-1.5 text-xs ${
              format === f ? 'bg-sky text-ink font-semibold' : 'bg-ink text-muted'
            }`}
          >
            {f === 'FREE_TEXT' ? 'Free text' : 'Multiple choice'}
          </button>
        ))}
      </div>

      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="The question"
        rows={2}
        className="w-full rounded bg-ink px-2 py-1.5 outline-none"
      />

      <MediaField token={token} value={media} onChange={setMedia} />

      {format === 'MULTIPLE_CHOICE' ? (
        <>
          {options.map((o, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={o}
                maxLength={MAX_ANSWER_LENGTH}
                onChange={(e) => {
                  const next = [...options];
                  next[i] = e.target.value;
                  setOptions(next);
                }}
                placeholder={`Option ${i + 1}`}
                className="flex-1 rounded bg-ink px-2 py-1.5 outline-none"
              />
              <button
                onClick={() => setCorrectAnswer(o)}
                disabled={!o}
                className={`w-9 rounded text-xs disabled:opacity-30 ${
                  correctAnswer === o && o ? 'bg-go text-ink' : 'bg-ink text-muted'
                }`}
                title="Mark as the correct answer"
              >
                ✓
              </button>
            </div>
          ))}
          {/* The single most common authoring mistake, caught before it is saved. */}
          {correctAnswer && !options.includes(correctAnswer) && (
            <p className="text-xs text-stop">
              Pick the correct answer from the options above &mdash; it must match verbatim.
            </p>
          )}
        </>
      ) : (
        <>
          <input
            value={correctAnswer}
            maxLength={MAX_ANSWER_LENGTH}
            onChange={(e) => setCorrectAnswer(e.target.value)}
            placeholder="The answer"
            className="w-full rounded bg-ink px-2 py-1.5 outline-none"
          />
          <input
            value={accepted}
            onChange={(e) => setAccepted(e.target.value)}
            placeholder="Also accept (comma separated)"
            className="w-full rounded bg-ink px-2 py-1.5 outline-none"
          />
        </>
      )}

      <div className="flex gap-2">
        <input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="Topic"
          list="topic-suggestions"
          className="flex-1 rounded bg-ink px-2 py-1.5 outline-none"
        />
        {/* A datalist suggests without constraining: pick one, or type your own
            and it joins the list for everyone next time. */}
        <datalist id="topic-suggestions">
          {SUGGESTED_TOPIC_GROUPS.flatMap((g) =>
            g.topics.map((t) => <option key={t} value={t} label={`${t} — ${g.group}`} />),
          )}
        </datalist>
        <select
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value as Difficulty)}
          className="rounded bg-ink px-2 py-1.5 outline-none"
        >
          {(['EASY', 'MEDIUM', 'HARD'] as const).map((d) => (
            <option key={d} value={d}>
              {d.toLowerCase()}
            </option>
          ))}
        </select>
      </div>

      <label className="flex items-center gap-2 text-xs text-muted">
        <input
          type="checkbox"
          checked={needsReview}
          onChange={(e) => setNeedsReview(e.target.checked)}
          className="accent-flag"
        />
        Still needs checking by a person
      </label>

      {error && <p className="text-xs text-stop">{error}</p>}

      <div className="flex gap-2">
        <button onClick={() => void save()} className="flex-1 rounded bg-go py-2 font-semibold text-ink">
          {existing ? 'Save changes' : 'Save'}
        </button>
        <button onClick={onCancel} className="rounded bg-ink px-4 py-2 text-muted">
          Cancel
        </button>
      </div>
    </div>
  );
}

const clip = (s: string, n = 40) => (s.length <= n ? s : `${s.slice(0, n - 1)}…`);

/**
 * PATCH replaces the whole question rather than merging, so anything that edits
 * one field has to send the rest back with it. Every field the create schema
 * takes is already on the view, so this is a straight narrowing — and keeping it
 * in one place means a new column cannot be quietly dropped by one caller.
 */
function toQuestionBody(q: AdminQuestionView) {
  return {
    format: q.format,
    prompt: q.prompt,
    options: q.options,
    correctAnswer: q.correctAnswer,
    acceptedAnswers: q.acceptedAnswers,
    pointsValue: q.pointsValue,
    explanation: q.explanation,
    topic: q.topic,
    difficulty: q.difficulty,
    needsReview: q.needsReview,
  };
}

function asDetails(details: unknown): string[] | null {
  if (!Array.isArray(details)) return null;
  return details.map((d: { message?: string }) => d.message ?? '').filter(Boolean);
}
